/**
 * CORE-DEMO-JOURNEY-STITCH-V1 (V7-P4 Slice 3) -- THE LEAD 360 mapper.
 *
 * Standard 1 (record-anatomy adoption / the portability test cashing in): a
 * PURE mapper that feeds the SAME generic record primitives the Patient 360
 * surface uses -- `HighlightsPanel` (via `buildLeadHighlights`), `Breadcrumbs`
 * (via `leadRecordBreadcrumbs`) -- WITHOUT a single new primitive. It reads
 * ONLY the already-resolved current-truth contract (record/current-truth.ts) so
 * it can never disagree with the CurrentStateBand (they consume the SAME
 * `truth`), and it fabricates nothing.
 *
 * PURE by construction: no React, no fetch, no Supabase, no `Date.now()` -- the
 * caller resolves `truth` once and hands it in, so every helper unit-tests
 * without jsdom and the drawer/page/parity renders stay byte-consistent.
 *
 * SECURITY POSTURE: nothing here is an access decision. `leadLinkedRecords`
 * gates a nav affordance on an EXISTING role signal for UX only (disabled-with-
 * reason, never absent) -- it never widens a read; the SELECT-level role fence
 * and the visibleSections manifest remain the boundary.
 *
 * NAME NOTE: this is the leads-directory Lead 360 mapper; do NOT confuse it
 * with owner/lead-360-view.ts (`buildLead360View`) -- distinct exports, no
 * re-export.
 */

import type { AcBadgeTone, BreadcrumbItem, HighlightFact } from '@/dashboard/ui';
import { patientRecordHref } from '@/dashboard/patients/patient-master-view';
import {
  NEXT_ACTION_UNKNOWN_LABEL,
  STAGE_NEXT_ACTION,
} from '@/dashboard/leads/lead-lifecycle-rail-view';
import type {
  ConversionKind,
  LeadRecordCurrentTruth,
  NextActionSource,
  PaymentStateKind,
} from '@/dashboard/record/current-truth';

// Tone maps (mirror of the CurrentStateBand's, so the highlights band and the
// bespoke band read the same tones; kept tiny + local -- the band's maps are
// not exported).
const CONVERSION_TONE: Readonly<Record<ConversionKind, AcBadgeTone>> = {
  none: 'neutral',
  awaiting_decision: 'info',
  converted: 'positive',
  partially_accepted: 'info',
  not_converted: 'danger',
  contradiction: 'danger',
};

const PAYMENT_TONE: Readonly<Record<PaymentStateKind, AcBadgeTone>> = {
  recorded: 'positive',
  collect_pending: 'pending',
  advised_only_no_handoff: 'info',
  none: 'neutral',
  unknown_on_this_read: 'neutral',
};

export const LEAD_NO_APPOINTMENT_LABEL = 'No appointment';
export const LEAD_NO_FOLLOW_UP_LABEL = 'No follow-up due';

/**
 * Build the Lead 360 highlights band -- the five-second decision set for a lead
 * (appointment, outcome, money, patient record, follow-up, CRM stage), in scan
 * order with the money/outcome emphasis tier. Mirrors buildPatientHighlights
 * (patient-360-view.ts) so the SAME HighlightsPanel primitive renders both.
 * Honest by construction: every fact reads a resolved contract field.
 */
export function buildLeadHighlights(
  truth: LeadRecordCurrentTruth,
): readonly HighlightFact[] {
  return [
    {
      key: 'appointment',
      label: 'Appointment',
      value: truth.currentAppointment.statusLabel ?? LEAD_NO_APPOINTMENT_LABEL,
      tone: truth.currentAppointment.kind === 'none' ? 'neutral' : 'brand',
      emphasis: truth.currentAppointment.kind !== 'none',
      testId: 'lead360-highlight-appointment',
    },
    {
      key: 'outcome',
      label: 'Outcome',
      value: truth.currentConversionState.label,
      tone: CONVERSION_TONE[truth.currentConversionState.kind],
      emphasis: true,
      testId: 'lead360-highlight-outcome',
    },
    {
      key: 'payment',
      label: 'Payment',
      value: truth.currentPaymentState.label,
      tone: PAYMENT_TONE[truth.currentPaymentState.kind],
      emphasis: true,
      testId: 'lead360-highlight-payment',
    },
    {
      key: 'identity',
      label: 'Patient record',
      value: truth.identityState.label,
      tone: truth.identityState.kind === 'linked' ? 'positive' : 'neutral',
      testId: 'lead360-highlight-identity',
    },
    {
      key: 'followup',
      label: 'Follow-up',
      value: truth.currentFollowUpState.whyLabel ?? LEAD_NO_FOLLOW_UP_LABEL,
      tone: truth.currentFollowUpState.kind === 'required' ? 'info' : 'neutral',
      testId: 'lead360-highlight-followup',
    },
    {
      key: 'stage',
      label: 'CRM stage',
      value: truth.currentLeadStage.label,
      tone: 'neutral',
      testId: 'lead360-highlight-stage',
    },
  ];
}

// --- Today's desk action vs the LEAD next action (F1 / #290) -----------------

export interface LeadActionView {
  readonly label: string;
  readonly source: NextActionSource;
  readonly kind: 'desk' | 'lead';
}

export interface LeadNextActionSplit {
  /** The operational desk step (arrival/consult/collect); null when the record
   *  is past desk work and only the CRM forward step remains. */
  readonly deskAction: LeadActionView | null;
  /** The lead/CRM continuity forward step -- always present (the lead surface
   *  never dead-ends), so an unfinished journey never reads "No action
   *  required". */
  readonly nextAction: LeadActionView;
}

/** The operational (desk-owned) next-action sources. */
const DESK_SOURCES: readonly NextActionSource[] = ['appointment', 'visit', 'payment'];

/**
 * F1 / #290 for the lead surface: separate TODAY'S DESK action (operational --
 * what the desk does next) from the LEAD next action (the CRM continuity /
 * commercial forward step). Reads ONLY the resolved truth; invents no state.
 *
 * NEGATIVE-PIN GUARANTEE: the lead surface's `nextLawfulAction` always resolves
 * a lawful step (never "No action required"), and this split preserves that --
 * `nextAction` is always a real label. An unfinished journey (open appointment,
 * required follow-up, undecided/partial/contradiction conversion, collect
 * pending) therefore never renders an idle "No action required".
 */
export function splitLeadDeskVsNextAction(
  truth: LeadRecordCurrentTruth,
): LeadNextActionSplit {
  const nla = truth.nextLawfulAction;
  const isDesk = DESK_SOURCES.includes(nla.source);
  const deskAction: LeadActionView | null = isDesk
    ? { label: nla.label, source: nla.source, kind: 'desk' }
    : null;

  let nextAction: LeadActionView;
  if (!isDesk) {
    // conversion / stage / unknown -> the next lawful action IS the lead step.
    nextAction = { label: nla.label, source: nla.source, kind: 'lead' };
  } else if (truth.currentLeadStage.stage !== null) {
    // Mid-desk-work: the lead-journey forward step from the recorded stage.
    nextAction = {
      label: STAGE_NEXT_ACTION[truth.currentLeadStage.stage],
      source: 'stage',
      kind: 'lead',
    };
  } else {
    nextAction = { label: NEXT_ACTION_UNKNOWN_LABEL, source: 'unknown', kind: 'lead' };
  }
  return { deskAction, nextAction };
}

// --- Record-anatomy breadcrumbs (slice-2 grammar) ----------------------------

/**
 * The lawful back-path trail for a lead record: Leads -> this lead. Uses the
 * shared Breadcrumbs grammar (the terminal crumb is the current page, no `to`).
 */
export function leadRecordBreadcrumbs(
  safeLeadId: string,
  opts?: { readonly leadsHref?: string },
): readonly BreadcrumbItem[] {
  return [
    { label: 'Leads', to: opts?.leadsHref ?? '/dashboard/leads' },
    { label: `Lead #${safeLeadId}`, key: 'current' },
  ];
}

// --- Journey continuity: linked records one click away (Standard 3) ----------

export interface LeadLinkedRecord {
  readonly key: string;
  readonly label: string;
  /** The lawful destination, or null when the role cannot open it. */
  readonly href: string | null;
  /** Why the target is disabled for this role (never absent -- disabled-with-
   *  reason). Null when the link is enabled. */
  readonly disabledReason: string | null;
  readonly testId: string;
}

export const LEAD_PATIENT_LINK_DENIED_REASON =
  'Your role cannot open the patient record';

/**
 * The records lawfully reachable one click away from this lead (Standard 3:
 * journey continuity). Today: the linked patient record, when the lead is
 * linked to one. An unlawful-for-role target renders DISABLED-WITH-REASON,
 * never absent (`canOpenPatient` is an EXISTING UX signal, never a read
 * widen). A possible-match / unlinked lead has no patient record to reach yet,
 * so no chip is emitted (there is genuinely no linked record).
 */
export function leadLinkedRecords(
  truth: LeadRecordCurrentTruth,
  canOpenPatient: boolean,
): readonly LeadLinkedRecord[] {
  const records: LeadLinkedRecord[] = [];
  if (
    truth.identityState.kind === 'linked' &&
    truth.stableIds.patientRecordId !== null
  ) {
    records.push({
      key: 'patient',
      label: truth.identityState.umr
        ? `Patient ${truth.identityState.umr}`
        : 'Patient record',
      href: canOpenPatient
        ? patientRecordHref(truth.stableIds.patientRecordId)
        : null,
      disabledReason: canOpenPatient ? null : LEAD_PATIENT_LINK_DENIED_REASON,
      testId: 'lead360-linked-patient',
    });
  }
  return records;
}
